import React, { createContext, useContext, useState, useEffect } from 'react';
import { ProjectService } from '../core/application/ProjectService';
import { RecordService } from '../core/application/RecordService';
import { fileRepository } from '../core/repositories/fileRepository';

const ProjectContext = createContext(null);

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [projectRecords, setProjectRecords] = useState([]);
  const [projectFiles, setProjectFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  // واکشی مجدد داده‌ها از لایه سرویس کاربردی
  const syncStateWithServices = async () => {
    setLoading(true);
    try {
      const allProjects = await ProjectService.getProjectsList();
      setProjects(allProjects);

      const activeId = await ProjectService.getActiveProjectId();
      const current = allProjects.find((p) => p.id === activeId) || allProjects[0] || null;
      setActiveProject(current);

      if (current) {
        const [records, files] = await Promise.all([
          RecordService.getActiveRecords(current.id),
          fileRepository.getByProject(current.id)
        ]);
        setProjectRecords(records);
        setProjectFiles(files);
      }
    } catch (err) {
      console.error('[ProjectContext Adapter Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    syncStateWithServices();
  }, []);

  const selectActiveProject = async (id) => {
    await ProjectService.setActiveProjectId(id);
    const target = projects.find((p) => p.id === id) || null;
    setActiveProject(target);
    if (target) {
      const [records, files] = await Promise.all([
        RecordService.getActiveRecords(target.id),
        fileRepository.getByProject(target.id)
      ]);
      setProjectRecords(records);
      setProjectFiles(files);
    }
  };

  const createNewProject = async (metadata) => {
    const created = await ProjectService.createProject(metadata);
    await selectActiveProject(created.id);
    await syncStateWithServices();
    return created;
  };

  const removeProject = async (id) => {
    await ProjectService.deleteProjectCascade(id);
    await syncStateWithServices();
  };

  const saveRecordToActive = async (type, payload) => {
    if (!activeProject) return null;
    const rec = await RecordService.createRecord(activeProject.id, type, payload);
    const updated = await RecordService.getActiveRecords(activeProject.id);
    setProjectRecords(updated);
    return rec;
  };

  const reviseRecordInActive = async (rootId, updatedPayload, reason) => {
    if (!activeProject) return null;
    const rec = await RecordService.reviseRecord(activeProject.id, rootId, updatedPayload, reason);
    const updated = await RecordService.getActiveRecords(activeProject.id);
    setProjectRecords(updated);
    return rec;
  };

  const saveFileToActive = async (fileBlob, metadata) => {
    if (!activeProject) return null;
    const saved = await fileRepository.saveFile(activeProject.id, fileBlob, metadata);
    const updated = await fileRepository.getByProject(activeProject.id);
    setProjectFiles(updated);
    return saved;
  };

  return (
    <ProjectContext.Provider
      value={{
        projects,
        activeProject,
        projectRecords,
        projectFiles,
        loading,
        selectActiveProject,
        createNewProject,
        removeProject,
        saveRecordToActive,
        reviseRecordInActive,
        saveFileToActive,
        refreshAll: syncStateWithServices
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used within ProjectProvider');
  return ctx;
};
